import type { GameDefinition, Outcome, Rng, Seat, Viewer } from '@campfire/engine';
import { deal, shuffle, standardDeck, type Card } from '@campfire/cards';
import { followable, newTable, playCard, sortHand, teamOf, turnOf, type CompletedTrick, type Trick, type TrickRules, type TrickTable } from '@campfire/tricks';

export interface SpadesOptions {
  /** A team at or above this score wins; at or below its negative loses. */
  readonly target: number;
  /** Allow bidding nil (0): +100 if you take no tricks, −100 otherwise. */
  readonly nil: boolean;
}

export interface TeamHandResult {
  readonly bid: number;
  readonly tricks: number;
  readonly made: boolean;
  /** Points from the contract, nils and bag penalties. */
  readonly points: number;
  readonly bags: number;
  readonly bagPenalty: boolean;
  readonly nils: readonly { readonly seat: Seat; readonly made: boolean }[];
}

export type SpadesEvent = { readonly kind: 'hand'; readonly hand: number; readonly teams: readonly [TeamHandResult, TeamHandResult] };

export interface SpadesState {
  readonly options: SpadesOptions;
  readonly phase: 'bid' | 'play';
  readonly hand: number;
  readonly dealer: Seat;
  /** Bid per seat; null = not bid yet. 0 = nil. */
  readonly bids: readonly (number | null)[];
  readonly table: TrickTable;
  readonly spadesBroken: boolean;
  /** Team scores [seats 0 & 2, seats 1 & 3]. */
  readonly scores: readonly [number, number];
  /** Accumulated overtricks per team. */
  readonly bags: readonly [number, number];
  readonly last: SpadesEvent | null;
  readonly result: Outcome | null;
}

export type SpadesAction = { readonly type: 'bid'; readonly bid: number } | { readonly type: 'play'; readonly card: string };

export interface SpadesView {
  readonly you: Seat | null;
  readonly phase: 'bid' | 'play';
  readonly hand: readonly Card[] | null;
  readonly legal: readonly string[];
  /** Bids you may make now. */
  readonly bidChoices: readonly number[];
  readonly handCounts: readonly number[];
  readonly dealer: Seat;
  readonly bids: readonly (number | null)[];
  readonly turn: Seat | null;
  readonly trick: Trick;
  readonly lastTrick: CompletedTrick | null;
  readonly won: readonly number[];
  readonly spadesBroken: boolean;
  readonly scores: readonly [number, number];
  readonly bags: readonly [number, number];
  readonly target: number;
  readonly handNo: number;
  readonly last: SpadesEvent | null;
  readonly result: Outcome | null;
}

const RULES: TrickRules = { trump: 'S' };

function newHand(options: SpadesOptions, hand: number, dealer: Seat, scores: readonly [number, number], bags: readonly [number, number], rng: Rng, last: SpadesEvent | null): SpadesState {
  const { hands } = deal(shuffle(standardDeck(), rng), 4, { perHand: 13, startHand: (dealer + 1) % 4 });
  const sorted = hands.map((h) => sortHand(h, RULES));
  return {
    options,
    phase: 'bid',
    hand,
    dealer,
    bids: [null, null, null, null],
    table: newTable(sorted, (dealer + 1) % 4),
    spadesBroken: false,
    scores,
    bags,
    last,
    result: null,
  };
}

/** Whose bid it is: clockwise from the dealer's left (FR-SPADES-02). */
const bidder = (s: SpadesState): Seat => (s.dealer + 1 + s.bids.filter((b) => b !== null).length) % 4;

export function bidChoices(s: SpadesState): number[] {
  return Array.from({ length: 14 }, (_, i) => i).filter((b) => b > 0 || s.options.nil);
}

/** Cards `seat` may play now (FR-SPADES-03). */
export function legalCards(s: SpadesState, seat: Seat): Card[] {
  const hand = s.table.hands[seat] ?? [];
  if (s.table.trick.plays.length > 0) return followable(hand, s.table.trick, RULES);
  if (s.spadesBroken) return [...hand];
  const other = hand.filter((c) => c.suit !== 'S');
  return other.length ? other : [...hand];
}

/** Score one team's hand (FR-SPADES-04/05/06). */
export function scoreTeam(bids: readonly number[], tricks: readonly number[], seats: readonly Seat[], bagsBefore: number): TeamHandResult {
  let points = 0;
  let contract = 0;
  let taken = 0;
  let nilTricks = 0;
  const nils: { seat: Seat; made: boolean }[] = [];
  for (const seat of seats) {
    const b = bids[seat] ?? 0;
    const t = tricks[seat] ?? 0;
    if (b === 0) {
      nils.push({ seat, made: t === 0 });
      points += t === 0 ? 100 : -100;
      nilTricks += t;
    } else {
      contract += b;
      taken += t;
    }
  }
  const made = taken >= contract;
  // A failed nil's tricks don't help the partner's contract, but they do count as bags.
  const over = (made ? taken - contract : 0) + nilTricks;
  points += made ? 10 * contract + over : -10 * contract + nilTricks;
  let bags = bagsBefore + over;
  const bagPenalty = bags >= 10;
  while (bags >= 10) {
    points -= 100;
    bags -= 10;
  }
  return { bid: contract, tricks: taken + nilTricks, made, points, bags, bagPenalty, nils };
}

function endHand(s: SpadesState, rng: Rng): SpadesState {
  const bids = s.bids.map((b) => b ?? 0);
  const teams = [scoreTeam(bids, s.table.won, [0, 2], s.bags[0]), scoreTeam(bids, s.table.won, [1, 3], s.bags[1])] as const;
  const scores: [number, number] = [s.scores[0] + teams[0].points, s.scores[1] + teams[1].points];
  const bags: [number, number] = [teams[0].bags, teams[1].bags];
  const last: SpadesEvent = { kind: 'hand', hand: s.hand, teams };
  const t = s.options.target;
  const over = scores.some((v) => v >= t || v <= -t);
  if (over && scores[0] !== scores[1]) {
    const win = scores[0] > scores[1] ? 0 : 1;
    return {
      ...s,
      scores,
      bags,
      last,
      result: { winners: [win, win + 2], draw: false, reason: scores[win] >= t ? 'target' : 'opponents fell too far behind', scores: { 0: scores[0], 1: scores[1], 2: scores[0], 3: scores[1] } },
    };
  }
  return newHand(s.options, s.hand + 1, (s.dealer + 1) % 4, scores, bags, rng, last);
}

export const spades: GameDefinition<SpadesState, SpadesAction, SpadesView, SpadesOptions> = {
  id: 'spades',
  name: 'Spades',
  rules: [
    'Four players in two teams: seats 1 and 3 against seats 2 and 4, partners sitting across. Spades are always trump.',
    'Each player is dealt 13 cards. Starting left of the dealer, each player bids the number of tricks they expect to win. Your team’s bid is the two bids added together.',
    'The player left of the dealer leads. You must follow suit if you can. The highest spade wins, otherwise the highest card of the suit that was led.',
    'You may not lead a spade until a spade has been played (spades are broken), unless all you hold is spades.',
    'Scoring: a team that makes its bid scores 10 per bid trick, plus 1 for each extra trick (a bag). Miss it and you lose 10 per bid trick.',
    'Every 10 bags a team collects costs 100 points.',
    'Setting: Nil bids. Allowed by default: bid nil (0) and you win +100 if you take no tricks, or lose 100 if you take any. Your partner’s bid still counts for the team. There is no blind nil.',
    'Setting: Game length. A team wins at 500 (or 300) points, or when the other team falls to minus that score.',
    'The app does all the scoring. Table talk about your hands is on the honour system.',
  ],
  minSeats: 4,
  maxSeats: 4,
  defaultOptions: { target: 500, nil: true },
  optionSpecs: [
    {
      key: 'target',
      label: 'Game length',
      help: 'Partners (across from each other) win at this score, or when the other team falls to minus this score.',
      choices: [
        { value: 500, label: 'To 500 points' },
        { value: 300, label: 'To 300 points' },
      ],
    },
    {
      key: 'nil',
      label: 'Nil bids',
      choices: [
        { value: true, label: 'Allowed (±100)' },
        { value: false, label: 'Not allowed' },
      ],
    },
  ],

  setup({ options, rng }) {
    return newHand(options, 1, 0, [0, 0], [0, 0], rng, null);
  },

  activeSeats(s) {
    if (s.result) return [];
    return [s.phase === 'bid' ? bidder(s) : turnOf(s.table)];
  },

  validate(s, a, seat) {
    if (a.type === 'bid') {
      if (s.phase !== 'bid') return { ok: false, reason: 'bidding is over' };
      if (seat !== bidder(s)) return { ok: false, reason: 'not your turn to bid' };
      if (!bidChoices(s).includes(a.bid)) return { ok: false, reason: s.options.nil ? 'bid 0 (nil) to 13' : 'bid 1 to 13' };
      return { ok: true };
    }
    if (a.type !== 'play') return { ok: false, reason: 'unknown action' };
    if (s.phase !== 'play') return { ok: false, reason: 'finish bidding first' };
    if (seat !== turnOf(s.table)) return { ok: false, reason: 'not your turn' };
    if (!(s.table.hands[seat] ?? []).some((c) => c.id === a.card)) return { ok: false, reason: 'you do not hold that card' };
    if (!legalCards(s, seat).some((c) => c.id === a.card)) {
      return { ok: false, reason: s.table.trick.plays.length ? 'you must follow suit' : 'spades are not broken yet' };
    }
    return { ok: true };
  },

  apply(s, a, { rng }) {
    if (a.type === 'bid') {
      const seat = bidder(s);
      const bids = s.bids.map((b, i) => (i === seat ? a.bid : b));
      return { ...s, bids, phase: bids.every((b) => b !== null) ? 'play' : 'bid' };
    }
    const seat = turnOf(s.table);
    const card = (s.table.hands[seat] as readonly Card[]).find((c) => c.id === a.card) as Card;
    const { table, completed } = playCard(s.table, seat, a.card, RULES, 4);
    const st: SpadesState = { ...s, table, spadesBroken: s.spadesBroken || card.suit === 'S' };
    return completed && table.hands.every((h) => h.length === 0) ? endHand(st, rng) : st;
  },

  view(s, viewer: Viewer): SpadesView {
    const you = viewer.kind === 'seat' ? viewer.seat : null;
    const turn = s.result ? null : s.phase === 'bid' ? bidder(s) : turnOf(s.table);
    const mine = you !== null && you === turn;
    return {
      you,
      phase: s.phase,
      hand: you === null ? null : (s.table.hands[you] ?? null),
      legal: mine && s.phase === 'play' ? legalCards(s, you).map((c) => c.id) : [],
      bidChoices: mine && s.phase === 'bid' ? bidChoices(s) : [],
      handCounts: s.table.hands.map((h) => h.length),
      dealer: s.dealer,
      bids: s.bids,
      turn,
      trick: s.table.trick,
      lastTrick: s.table.lastTrick,
      won: s.table.won,
      spadesBroken: s.spadesBroken,
      scores: s.scores,
      bags: s.bags,
      target: s.options.target,
      handNo: s.hand,
      last: s.last,
      result: s.result,
    };
  },

  outcome: (s) => s.result,

  enumerateActions(s, seat) {
    if (s.result) return [];
    if (s.phase === 'bid') return seat === bidder(s) ? bidChoices(s).map((bid) => ({ type: 'bid', bid })) : [];
    if (seat !== turnOf(s.table)) return [];
    return legalCards(s, seat).map((c) => ({ type: 'play', card: c.id }));
  },
};

export { teamOf };
